import {
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Text,
  TextProps,
  useBreakpointValue,
} from '@chakra-ui/react';
import { atom, useAtom, useSetAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';

import { DASHBOARD } from '../../../constants/urls';
import {
  Car,
  DealType,
  ExternalDeal,
  Maybe,
  useUserDealsQuery,
} from '../../../gql/generated/graphql';
import useHorizontalScrollFade from '../../../hooks/useHorizontalScrollFade';
import { sameEmail } from '../../../utils/customer';
import { DealIdentifier, NEW_DEAL_VALUE, dashboardDealInfoAtom } from '../RootDashboard';

const sameDeal = (first: DealIdentifier | undefined, second: DealIdentifier | undefined) =>
  first?.id === second?.id && !!first?.isCobuyer === !!second?.isCobuyer;

type DealSwitcherOption = {
  value: DealIdentifier;
  label: string;
};

export const dealSwitcherOptionAtom = atom<DealSwitcherOption[]>([]);

type Props = {
  inFlow: boolean;
  car: Maybe<Car>;
};

const CoBuyerBadge = (props: TextProps) => (
  <Text
    as="span"
    bg="leaseEndBlue"
    color="white"
    px="5px"
    py="1px"
    borderRadius="full"
    fontSize="2xs"
    lineHeight={1}
    ml={0}
    my={0}
    verticalAlign="middle"
    display="inline-block"
    {...props}
  >
    CO-BUYER
  </Text>
);

type PillLabelProps = {
  year: string;
  makeModel: string;
  showBadge: boolean;
};

const PillLabel = ({ year, makeModel, showBadge }: PillLabelProps) => {
  return (
    <>
      <Text fontSize="2xs" fontWeight="bold" lineHeight="tight">
        {year}
      </Text>
      <Text textAlign="left" fontSize="xs" fontWeight="bold" lineHeight="tight">
        {makeModel}
      </Text>
      {showBadge && <CoBuyerBadge />}
    </>
  );
};

const PillDealSwitcher = ({ inFlow, car }: Props) => {
  const [addAnotherLeaseExpanded, setAddAnotherLeaseExpanded] = useState(false);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const isTablet = useBreakpointValue({ base: true, lg: false });
  // Mobile uses a horizontal scroller, so every pill stays visible (no overflow menu).
  const maxVisiblePills = isMobile ? Infinity : isTablet ? 2 : 4;
  const { data: userDeals, loading } = useUserDealsQuery({ fetchPolicy: 'network-only' });
  const [dealId, setDealId] = useAtom(dashboardDealInfoAtom);
  const isNewDeal = dealId?.id === NEW_DEAL_VALUE.id;
  const setDealSwitcherOptions = useSetAtom(dealSwitcherOptionAtom);
  const history = useHistory();

  const deals = useMemo(() => (userDeals?.userDeals as ExternalDeal[]) ?? [], [userDeals]);

  const { vehiclePills, userHasNonRefiDeal } = useMemo(() => {
    const pills = deals
      .filter((userDeal) => {
        if (userDeal?.isCobuyer && sameEmail(userDeal?.customer, userDeal?.cobuyer)) {
          return false;
        }
        return true;
      })
      .map((userDeal) => {
        const year = (userDeal?.car?.year ?? '').toUpperCase();
        const makeModel = [userDeal?.car?.make, userDeal?.car?.model]
          .filter(Boolean)
          .join(' ')
          .toUpperCase();
        return {
          value: {
            id: userDeal?.id || undefined,
            isCobuyer: userDeal?.isCobuyer,
          } as DealIdentifier,
          label: [year, makeModel].filter(Boolean).join(' '),
          year,
          makeModel,
          isCobuyer: !!userDeal?.isCobuyer,
          hasSameEmail: sameEmail(userDeal?.customer, userDeal?.cobuyer),
        };
      });

    const hasNonRefi = deals.some((deal) => deal?.type !== DealType.Refi);

    return { vehiclePills: pills, userHasNonRefiDeal: hasNonRefi };
  }, [deals]);

  const options = useMemo(() => {
    const dealOptions = deals.map((userDeal) => ({
      value: {
        id: userDeal?.id || undefined,
        isCobuyer: userDeal?.isCobuyer,
      } as DealIdentifier,
      label: [userDeal?.car?.year, userDeal?.car?.make, userDeal?.car?.model]
        .filter(Boolean)
        .join(' '),
    }));

    const hasNonRefi = deals.some((deal) => deal?.type !== DealType.Refi);

    return [
      ...dealOptions,
      ...(hasNonRefi
        ? [
            {
              value: NEW_DEAL_VALUE as DealIdentifier,
              label: inFlow
                ? `${car?.year} ${car?.make} ${car?.model}`.toUpperCase()
                : 'End Another Lease',
            },
          ]
        : []),
    ];
  }, [deals, inFlow, car]);

  useEffect(() => {
    setDealSwitcherOptions(options);
  }, [options, setDealSwitcherOptions]);

  const currentDeal = options.find(({ value }) => sameDeal(value, dealId)) || options[0];

  useEffect(() => {
    if (isNewDeal) {
      setAddAnotherLeaseExpanded(true);
    } else {
      setAddAnotherLeaseExpanded(false);
    }
    if (userDeals?.userDeals?.length && currentDeal && !sameDeal(currentDeal.value, dealId)) {
      setDealId(currentDeal.value);
    }
  }, [userDeals, currentDeal, isNewDeal, dealId, setDealId]);

  const handleClick = (value: DealIdentifier) => {
    history.push(DASHBOARD);
    setDealId(value);
  };

  const showExpandedLease = addAnotherLeaseExpanded && !isMobile;

  const { visiblePills, overflowPills } = useMemo(() => {
    if (vehiclePills.length <= maxVisiblePills) {
      return { visiblePills: vehiclePills, overflowPills: [] };
    }

    const activeIndex = vehiclePills.findIndex(
      (p) => p.value.id === dealId?.id && (sameDeal(p.value, dealId) || p.hasSameEmail),
    );

    // If active is already in the visible range, no reordering needed
    if (activeIndex < maxVisiblePills) {
      return {
        visiblePills: vehiclePills.slice(0, maxVisiblePills),
        overflowPills: vehiclePills.slice(maxVisiblePills),
      };
    }

    // Active pill is in overflow — swap it with the last visible pill
    const reordered = [...vehiclePills];
    [reordered[maxVisiblePills - 1], reordered[activeIndex]] = [
      reordered[activeIndex],
      reordered[maxVisiblePills - 1],
    ];
    const visible = reordered.slice(0, maxVisiblePills);
    const overflow = reordered.slice(maxVisiblePills);

    return { visiblePills: visible, overflowPills: overflow };
  }, [vehiclePills, dealId, maxVisiblePills]);

  const { ref: scrollerRef, maskImage, fade } = useHorizontalScrollFade();
  const inProgressPillIsLast = userHasNonRefiDeal && inFlow && !!car;
  const fadeMask = inProgressPillIsLast && !fade.right ? undefined : maskImage;

  const renderedPills = visiblePills.map(
    ({ value, year, makeModel, isCobuyer, hasSameEmail }, i) => {
      const active = value.id === dealId?.id;
      return (
        <Button
          py={4}
          variant="ghost"
          borderRightRadius="full"
          px={9}
          ml={isMobile && i === 0 ? 0 : -7}
          h="full"
          bg={active ? 'royalBlue' : 'gray.200'}
          color={active ? 'white' : 'gray.700'}
          cursor={active ? 'default' : 'pointer'}
          _hover={{ bg: active ? 'royalBlue' : 'gray.300' }}
          onClick={() => {
            if (!active) handleClick(value);
          }}
          zIndex={10 - i}
          key={`${value.id}-${isCobuyer}`}
          boxShadow="inset -2px 0 0 2px white"
          flexDir="column"
          alignItems="flex-start"
          flexShrink={0}
        >
          <PillLabel year={year} makeModel={makeModel} showBadge={isCobuyer && !hasSameEmail} />
        </Button>
      );
    },
  );

  const overflowMenu = overflowPills.length > 0 && (
    <Menu>
      <MenuButton
        as={Button}
        py={4}
        variant="ghost"
        borderRightRadius="full"
        px={9}
        ml={-7}
        h="full"
        bg="gray.200"
        color="gray.700"
        _hover={{ bg: 'gray.300' }}
        _active={{ bg: 'gray.300' }}
        zIndex={10 - visiblePills.length}
        boxShadow="inset -2px 0 0 2px white"
      >
        <Text fontSize="14px" fontWeight="bold">
          + {overflowPills.length} more
        </Text>
      </MenuButton>
      <MenuList zIndex={20}>
        {overflowPills.map(({ value, year, makeModel, isCobuyer, hasSameEmail }) => (
          <MenuItem
            key={`overflow-${value.id}-${isCobuyer}`}
            onClick={() => handleClick(value)}
            fontWeight="bold"
            fontSize="sm"
          >
            {[year, makeModel].filter(Boolean).join(' ')}
            {isCobuyer && !hasSameEmail && <CoBuyerBadge />}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );

  const addLeaseButton = userHasNonRefiDeal && (
    <Button
      py={4}
      variant="ghost"
      borderRightRadius="full"
      pr={inFlow && car ? 9 : showExpandedLease ? 9 : 7}
      pl={inFlow && car ? 9 : showExpandedLease ? 9 : 12}
      ml={-7}
      mt="2px"
      h="calc(100% - 4px)"
      bg="leaseEndBlue"
      color="white"
      flexDir={inFlow && car ? 'column' : 'row'}
      alignItems={inFlow && car ? 'flex-start' : 'center'}
      justifyContent="center"
      whiteSpace="nowrap"
      overflow="hidden"
      flexShrink={0}
      position="relative"
      // On mobile this button lives outside the scroller, so it's always visible: it sits next to
      // the last pill when pills fit and stays pinned right once the scroller shrinks. The mask
      // carves a concave notch out of its left edge for the pill's convex right edge to nest into.
      sx={
        isMobile
          ? {
              maskImage: 'radial-gradient(26px 52% at 0% 50%, transparent 0 95%, #000 100%)',
              WebkitMaskImage: 'radial-gradient(26px 52% at 0% 50%, transparent 0 95%, #000 100%)',
            }
          : undefined
      }
      transition="all 0.6s ease"
      _hover={{ bg: 'leaseEndBlue', opacity: 0.9 }}
      onMouseEnter={isMobile ? undefined : () => setAddAnotherLeaseExpanded(true)}
      onMouseLeave={isMobile || isNewDeal ? undefined : () => setAddAnotherLeaseExpanded(false)}
      onClick={() => {
        setAddAnotherLeaseExpanded(true);
        handleClick(NEW_DEAL_VALUE as DealIdentifier);
      }}
    >
      {inFlow && car ? (
        <>
          <Text fontSize="sm" fontWeight="bold" textTransform="uppercase">
            {[car.year, car.make, car.model].filter(Boolean).join(' ')}
          </Text>
          <Text fontSize="xs" fontWeight="bold" color="leaseEndYellow">
            ↩ IN PROGRESS
          </Text>
        </>
      ) : (
        <>
          <Text
            fontSize="14px"
            fontWeight="bold"
            color="white"
            whiteSpace="nowrap"
            maxW={showExpandedLease ? '250px' : '0px'}
            opacity={showExpandedLease ? 1 : 0}
            overflow="hidden"
            transition="max-width 0.4s ease"
          >
            + END ANOTHER LEASE
          </Text>
          <Icon
            as={FaPlus}
            position="absolute"
            opacity={showExpandedLease ? 0 : 1}
            transition="opacity 0.3s ease"
          />
        </>
      )}
    </Button>
  );

  const inProgressPill = userHasNonRefiDeal && inFlow && car && (
    <Button
      py={4}
      variant="ghost"
      borderRightRadius="full"
      px={9}
      ml={-7}
      h="full"
      bg="leaseEndBlue"
      color="white"
      cursor={isNewDeal ? 'default' : 'pointer'}
      _hover={{ bg: 'leaseEndBlue', opacity: 0.9 }}
      boxShadow="inset -2px 0 0 2px white"
      flexDir="column"
      alignItems="flex-start"
      flexShrink={0}
      onClick={() => {
        if (isNewDeal) return;
        setAddAnotherLeaseExpanded(true);
        handleClick(NEW_DEAL_VALUE as DealIdentifier);
      }}
    >
      <Text fontSize="xs" fontWeight="bold" lineHeight="tight" textTransform="uppercase" px={2}>
        {[car.year, car.make, car.model].filter(Boolean).join(' ')}
      </Text>
      <Text fontSize="2xs" fontWeight="bold" color="leaseEndYellow" lineHeight="tight" px={2}>
        ↩ IN PROGRESS
      </Text>
    </Button>
  );

  return (
    <Skeleton
      isLoaded={!loading}
      borderRadius="full"
      h="full"
      display="flex"
      w={isMobile ? '100%' : undefined}
      minW={0}
    >
      {isMobile ? (
        <Flex alignItems="stretch" h="full" w="100%" minW={0}>
          <Flex
            ref={scrollerRef}
            alignItems="stretch"
            h="full"
            minW={0}
            flex="0 1 auto"
            overflowX="auto"
            borderRightRadius="full"
            boxShadow="inset -2px 0 0 2px white"
            sx={{
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              ...(fadeMask ? { maskImage: fadeMask, WebkitMaskImage: fadeMask } : {}),
            }}
          >
            {renderedPills}
            {inProgressPill}
          </Flex>
          {!(inFlow && car) && addLeaseButton}
        </Flex>
      ) : (
        <Flex alignItems="stretch" h="full">
          {renderedPills}
          {overflowMenu}
          {addLeaseButton}
        </Flex>
      )}
    </Skeleton>
  );
};

export default PillDealSwitcher;
